import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { 
  handleLegacyRedirect, 
  parseUrlParams, 
  getIdForDatabase,
  getCategoryFromSlug,
  getCategorySlug,
  createForumPostUrl 
} from '../../utils/urlUtils';

/**
 * Component to handle legacy URL redirects and URL validation
 */
export default function RedirectHandler() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleRedirect = async () => {
      const pathname = location.pathname;

      // Handle legacy /post/:uuid redirects
      const postMatch = pathname.match(/^\/post\/([a-f0-9-]+)$/);
      if (postMatch) {
        const postId = postMatch[1];
        try {
          const { data: post } = await supabase
            .from('posts')
            .select(`
              id,
              title,
              discussion_categories (name)
            `)
            .eq('id', postId)
            .single();

          if (post) {
            const category = post.discussion_categories?.name || 'general';
            const newUrl = createForumPostUrl(category, post.id, post.title);
            navigate(newUrl, { replace: true });
            return;
          }
        } catch (error) {
          console.error('Error fetching post for redirect:', error);
        }
        navigate('/forum', { replace: true });
        return;
      }

      // Handle legacy /forum/post/:id redirects
      const forumPostMatch = pathname.match(/^\/forum\/post\/(.+)$/);
      if (forumPostMatch) {
        const postId = forumPostMatch[1];
        try {
          const { data: post } = await supabase
            .from('posts')
            .select(`
              id,
              title,
              discussion_categories (name)
            `)
            .eq('id', postId)
            .single();

          if (post) {
            const category = post.discussion_categories?.name || 'general';
            const newUrl = createForumPostUrl(category, post.id, post.title);
            navigate(newUrl, { replace: true });
            return;
          }
        } catch (error) {
          console.error('Error fetching post for redirect:', error);
        }
        navigate('/forum', { replace: true });
        return;
      }

      // Handle numeric forum category URLs (e.g., /forum/reviews/1)
      const categoryMatch = pathname.match(/^\/forum\/([\w-]+)\/(\d+)$/);
      if (categoryMatch) {
        const [, categorySlug, numericId] = categoryMatch;
        try {
          const { data: post } = await supabase
            .from('posts')
            .select(`
              id,
              title,
              discussion_categories (name)
            `)
            .eq('id', numericId)
            .single();

          if (post) {
            const newUrl = createForumPostUrl(categorySlug, post.id, post.title);
            navigate(newUrl, { replace: true });
            return;
          }
        } catch (error) {
          console.error('Error fetching post for redirect:', error);
        }
        navigate(`/forum/${categorySlug}`, { replace: true });
        return;
      }

      // Handle old creator URLs
      const creatorMatch = pathname.match(/^\/creators\/([a-f0-9-]+)$/);
      if (creatorMatch && !pathname.includes('-')) {
        // This is a UUID-only creator URL, needs redirect
        navigate('/creators', { replace: true });
        return;
      }

      // Handle old drama URLs
      const dramaMatch = pathname.match(/^\/dramas\/([a-f0-9-]+)$/);
      if (dramaMatch && !pathname.includes('-')) {
        // This is a UUID-only drama URL, needs redirect
        navigate('/dramas', { replace: true });
        return;
      }
    };

    handleRedirect();
  }, [location.pathname, navigate]);

  return null; // This component doesn't render anything
}

/**
 * Hook to validate current URL and suggest corrections
 */
export function useUrlValidation() {
  const location = useLocation();
  const navigate = useNavigate();

  const validateCurrentUrl = async () => {
    const pathname = location.pathname;
    
    // Check if it's a forum post URL
    const forumMatch = pathname.match(/^\/forum\/([\w-]+)\/(.+)$/);
    if (forumMatch) {
      const [, categorySlug, urlParam] = forumMatch;
      
      try {
        const { id } = parseUrlParams(urlParam);
        const dbId = getIdForDatabase(id);
        
        // Fetch post to verify it exists and get correct title
        const { data: post } = await supabase
          .from('posts')
          .select(`
            id,
            title,
            discussion_categories (name)
          `)
          .eq('id', dbId)
          .single();

        if (post) {
          const correctCategory = getCategorySlug(post.discussion_categories?.name || 'general');
          const correctUrl = createForumPostUrl(correctCategory, post.id, post.title);
          
          // If current URL doesn't match the correct format, redirect
          if (pathname !== correctUrl) {
            navigate(correctUrl, { replace: true });
            return false;
          }
        }
      } catch (error) {
        console.error('URL validation error:', error);
        return false;
      }
    }
    
    return true;
  };

  return { validateCurrentUrl };
}